import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogBaseComponent } from '@/app/core/components';
import { OrganizationSelectorComponent } from '../entity-selection/organization-selector.component';
import { OrganizationResponseDto } from '@qc/shared';
import { OrganizationType } from '@qc/shared/enums';
import { ContactWithRole } from '../../interfaces/contact.interface';
import { Button } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TextareaModule } from 'primeng/textarea';

export interface OrganizationRoleData {
    organizationId: string;
    organizationName: string;
    roleType: OrganizationType;
    isPrimaryForRole: boolean;
    notes?: string;
    organizationDraft?: {
        organization: Partial<OrganizationResponseDto>;
        contacts: ContactWithRole[];
    };
}

@Component({
    selector: 'qc-add-organization-role-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        OrganizationSelectorComponent,
        Button,
        DropdownModule,
        TextareaModule,
    ],
    template: `
        <div class="w-[500px] max-w-[90vw] bg-white rounded-lg shadow-2xl">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">{{ editMode ? 'Edit' : 'Add' }} Organization Role</h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6">
                <form [formGroup]="organizationRoleForm" class="space-y-4">
                    <div>
                        <qc-organization-selector
                            label="Organization"
                            [required]="true"
                            [showCreateButton]="true"
                            [useInlineForm]="true"
                            [useTaggedDisplay]="true"
                            [deferSave]="deferSave"
                            [initialValue]="selectedOrganization"
                            (organizationSelected)="onOrganizationSelected($event)"
                            (organizationCleared)="onOrganizationCleared()"
                            (organizationCreated)="onOrganizationSelected($event)"
                            (organizationCreatedWithContacts)="onOrganizationCreatedWithContacts($event)"
                            (createModeChange)="onOrganizationCreateModeChange($event)"
                        />
                    </div>

                    <div *ngIf="!organizationSelectorInCreateMode">
                        <label class="block mb-2">Role Type *</label>
                        <p-dropdown
                            formControlName="roleType"
                            [options]="organizationTypeOptions"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select role type"
                            class="w-full"
                        />
                    </div>

                    <div *ngIf="!organizationSelectorInCreateMode">
                        <label class="block mb-2">Notes</label>
                        <textarea
                            pTextarea
                            formControlName="notes"
                            placeholder="Additional notes..."
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>
                </form>
            </div>

            <div *ngIf="!organizationSelectorInCreateMode" class="flex justify-end gap-2 p-6 border-t border-gray-200">
                <p-button label="Cancel" severity="secondary" variant="outlined" (onClick)="close()" />
                <p-button
                    label="{{ editMode ? 'Update' : 'Save' }}"
                    severity="primary"
                    (onClick)="save()"
                    [disabled]="!organizationRoleForm.valid || !selectedOrganization"
                />
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
        `,
    ],
})
export class AddOrganizationRoleDialogComponent extends DialogBaseComponent implements OnInit {
    @Input() editMode: boolean = false;
    @Input() organizationRoleToEdit: OrganizationRoleData | null = null;
    @Input() deferSave: boolean = false;
    @Output() organizationRoleAdded = new EventEmitter<OrganizationRoleData>();
    @Output() organizationRoleUpdated = new EventEmitter<OrganizationRoleData>();

    private fb = inject(FormBuilder);

    organizationRoleForm!: FormGroup;
    selectedOrganization: OrganizationResponseDto | null = null;
    organizationSelectorInCreateMode = false;
    organizationDraft: { organization: Partial<OrganizationResponseDto>; contacts: ContactWithRole[] } | null = null;

    organizationTypeOptions = Object.values(OrganizationType).map((value) => ({ label: value, value }));

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.initForm();
        if (this.editMode && this.organizationRoleToEdit) {
            this.populateFormForEdit();
        }
    }

    private initForm(): void {
        this.organizationRoleForm = this.fb.group({
            roleType: ['', Validators.required],
            isPrimaryForRole: [false],
            notes: [''],
        });
    }

    private populateFormForEdit(): void {
        if (this.organizationRoleToEdit) {
            this.organizationRoleForm.patchValue({
                roleType: this.organizationRoleToEdit.roleType,
                isPrimaryForRole: this.organizationRoleToEdit.isPrimaryForRole,
                notes: this.organizationRoleToEdit.notes,
            });

            // Set the selected organization
            this.selectedOrganization = {
                id: this.organizationRoleToEdit.organizationId,
                organizationName: this.organizationRoleToEdit.organizationName,
            } as OrganizationResponseDto;
        }
    }

    onOrganizationSelected(organization: OrganizationResponseDto): void {
        this.selectedOrganization = organization;
        this.organizationDraft = null;
    }

    onOrganizationCreatedWithContacts(data: { organization: OrganizationResponseDto; contacts: ContactWithRole[] }): void {
        if (!this.deferSave) {
            this.onOrganizationSelected(data.organization);
            return;
        }

        const tempOrgId = 'temp-org-' + Date.now();
        this.selectedOrganization = {
            ...data.organization,
            id: (data.organization as any).id || tempOrgId,
        } as OrganizationResponseDto;

        this.organizationDraft = {
            organization: data.organization,
            contacts: data.contacts || [],
        };
    }

    onOrganizationCleared(): void {
        this.selectedOrganization = null;
        this.organizationDraft = null;
    }

    onOrganizationCreateModeChange(isInCreateMode: boolean): void {
        this.organizationSelectorInCreateMode = isInCreateMode;
    }

    save(): void {
        if (this.organizationRoleForm.valid && this.selectedOrganization) {
            const roleData: OrganizationRoleData = {
                organizationId: this.selectedOrganization.id,
                organizationName: this.selectedOrganization.organizationName,
                roleType: this.organizationRoleForm.value.roleType,
                isPrimaryForRole: this.organizationRoleForm.value.isPrimaryForRole,
                notes: this.organizationRoleForm.value.notes,
                organizationDraft: this.organizationDraft || undefined,
            };

            if (this.editMode) {
                this.organizationRoleUpdated.emit(roleData);
            } else {
                this.organizationRoleAdded.emit(roleData);
            }
            this.close(true);
        }
    }
}
